import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ScopedPage } from '@/layouts/ScopedPage';
import { PasswordInput } from '@/components/PasswordInput';
import { hasCapability, useProjectScope } from '@/context/ProjectContext';
import { GitConnectionHelper, type GitConnectionValue } from '@/components/helpers/GitConnectionHelper';
import { TechStackHelper } from '@/components/helpers/TechStackHelper';
import { ConventionUploadHelper } from '@/components/helpers/ConventionUploadHelper';
import { MembersTab } from '@/pages/settings/MembersTab';
import { AgentTomlEditor, RetryEditor, SlaEditor } from '@/pages/settings/ConfigEditors';
import {
  useGetProjectDetailQuery,
  useUpdateProjectMutation,
  useGetProjectCredentialQuery,
  useSetProjectCredentialMutation,
  useListConventionsQuery,
  useUploadConventionMutation,
  useDeleteConventionMutation
} from '@/services/api';
import { parseApiError } from '@/utils/apiError';
import { useToast } from '@/components/toast/ToastProvider';
import { emptyTechStack, type TechStack } from '@/utils/techStack';
import type { CredentialMaskDto } from '@/types/api';


const TABS = [
  { key: 'basic', label: 'Basic info' },
  { key: 'git', label: 'Git & credentials' },
  { key: 'tech', label: 'Tech stack' },
  { key: 'conventions', label: 'Conventions' },
  { key: 'agents', label: 'Agent configuration' },
  { key: 'retry', label: 'Retry & timeout' },
  { key: 'sla', label: 'Set SLA' },
  { key: 'members', label: 'Members' }
] as const;

type TabKey = (typeof TABS)[number]['key'];

export function SettingsPage(): JSX.Element {
  const { project, user } = useProjectScope();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = (searchParams.get('tab') as TabKey | null) ?? 'basic';
  const canWrite = hasCapability(user, 'project:create') || user.role === 'admin';

  return (
    <ScopedPage title="Settings">
      <h1 className="page-title">Settings</h1>
      {!canWrite && (
        <div className="banner" role="status" style={{ marginTop: 16 }}>
          <span className="banner-icon">i</span>
          <span>You have read-only access to this project&apos;s settings.</span>
        </div>
      )}
      <div className="settings-layout" style={{ marginTop: 28 }}>
        <nav className="settings-tabs">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              className={`settings-tab ${tab === t.key ? 'active' : ''}`}
              onClick={() => setSearchParams({ tab: t.key })}
            >
              {t.label}
            </button>
          ))}
        </nav>
        <div>
          {tab === 'basic' && <BasicInfoSection projectId={project.project_id} canWrite={canWrite} />}
          {tab === 'git' && (
            <>
              <SettingsSection title="Git connection">
                <GitConnectionSection projectId={project.project_id} canWrite={canWrite} />
              </SettingsSection>
              <SettingsSection title="Codex API key">
                <CodexKeySection projectId={project.project_id} canWrite={canWrite} />
              </SettingsSection>
            </>
          )}
          {tab === 'tech' && (
            <SettingsSection title="Tech stack">
              <TechStackSettings projectId={project.project_id} canWrite={canWrite} />
            </SettingsSection>
          )}
          {tab === 'conventions' && (
            <SettingsSection title="Conventions">
              <ConventionsSettings projectId={project.project_id} canWrite={canWrite} />
            </SettingsSection>
          )}
          {tab === 'agents' && (
            <AgentTomlEditor projectId={project.project_id} canWrite={canWrite} />
          )}
          {tab === 'retry' && <RetryEditor projectId={project.project_id} canWrite={canWrite} />}
          {tab === 'sla' && <SlaEditor projectId={project.project_id} canWrite={canWrite} />}
          {tab === 'members' && <MembersTab projectId={project.project_id} canWrite={canWrite} />}
        </div>
      </div>
    </ScopedPage>
  );
}

function SettingsSection({ title, children }: { title: string; children: React.ReactNode }): JSX.Element {
  return (
    <div className="panel">
      <h2 className="panel-title">{title}</h2>
      <div style={{ marginTop: 12 }}>{children}</div>
    </div>
  );
}

function CredentialAccessNotice({ error }: { error: unknown }): JSX.Element | null {
  if (error === undefined || error === null) return null;
  const { status, message } = parseApiError(error);
  const forbidden = status === 403;
  return (
    <div className="banner error" role="status">
      <span className="banner-icon">!</span>
      <span>
        {forbidden
          ? 'You are not a member of this project, so its stored credentials are hidden. Ask a project admin to add you as a member to view or change them.'
          : message}
      </span>
    </div>
  );
}

function maskLabel(mask: CredentialMaskDto | undefined): string | null {
  if (!mask?.is_set) return null;
  if (!mask.prefix && !mask.last4) return 'configured · hidden';
  return `${mask.prefix ?? ''}…${mask.last4 ?? ''}`;
}

function CredentialStatus({ mask }: { mask: CredentialMaskDto | undefined }): JSX.Element | null {
  if (!mask) return null;
  if (!mask.is_set) {
    return <p className="field-help">Not set.</p>;
  }
  return (
    <p className="field-help">
      Stored
      {mask.updated_at ? ` · updated ${new Date(mask.updated_at).toLocaleDateString()}` : ''}.
      Typing a new value replaces it.
    </p>
  );
}

function GitConnectionSection({
  projectId,
  canWrite
}: {
  projectId: string;
  canWrite: boolean;
}): JSX.Element {
  const detailQ = useGetProjectDetailQuery(projectId);
  const credQ = useGetProjectCredentialQuery({ projectId, credential_type: 'git_https_token' });
  const [updateProject, { isLoading }] = useUpdateProjectMutation();
  const [setCredential, { isLoading: isSavingToken }] = useSetProjectCredentialMutation();
  const [pending, setPending] = useState<GitConnectionValue | null>(null);
  const [token, setToken] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [testAction, setTestAction] = useState<{
    run: () => void;
    disabled: boolean;
    busy: boolean;
  } | null>(null);
  const toast = useToast();

  const detail = detailQ.data;
  const mask = credQ.data;
  const fieldsChanged = pending !== null;
  const tokenChanged = token.trim() !== '';
  const dirty = fieldsChanged || tokenChanged;
  const busy = isLoading || isSavingToken;

  async function onSave(): Promise<void> {
    setError(null);
    try {
      if (tokenChanged) {
        await setCredential({
          projectId,
          credential_type: 'git_https_token',
          plaintext: token.trim()
        }).unwrap();
        setToken('');
      }
      const source = pending ?? {
        default_branch: detail?.default_branch ?? '',
        git_username: detail?.git_username ?? '',
        git_url: detail?.git_url ?? ''
      };
      {
        const branch = (source.default_branch ?? '').trim();
        const username = (source.git_username ?? '').trim();
        const url = (source.git_url ?? '').trim();
        const body: Record<string, string> = {
          default_branch: branch,
          git_username: username,
          git_repository_url: url
        };
        await updateProject({ projectId, body }).unwrap();
      }
      setPending(null);
      toast.success('Connection details updated.');
    } catch (err) {
      const message = parseApiError(err).message;
      setError(message);
      toast.error(message);
    }
  }

  return (
    <div>
      <GitConnectionHelper
        projectId={projectId}
        initial={{
          git_url: detail?.git_url ?? '',
          protocol: detail?.git_protocol ?? 'https',
          ...(detail?.git_username !== undefined ? { git_username: detail.git_username } : {}),
          ...(detail?.default_branch !== undefined
            ? { default_branch: detail.default_branch }
            : {}),
          verified: !!detail?.git_url
        }}
        showCredentialField={false}
        externalCredential={token.trim()}
        hideAction
        onAction={(a) => {
          setTestAction((prev) =>
            prev !== null &&
            prev.disabled === a.disabled &&
            prev.busy === a.busy &&
            prev.run === a.run
              ? prev
              : a
          );
        }}
        onConnected={(v) => setPending(v)}
      />
      <CredentialAccessNotice error={credQ.error} />
      <div className="field">
        <label className="field-label" htmlFor="git-token">
          Personal access token<span className="req">*</span>
        </label>
        <PasswordInput
          id="git-token"
          className="mono"
          toggleLabel="access token"
          placeholder={maskLabel(mask) ?? 'HTTPS access token'}
          {...(mask?.is_set && mask.length !== undefined ? { maskLength: mask.length } : {})}
          {...(maskLabel(mask) !== null ? { maskHint: maskLabel(mask) as string } : {})}
          value={token}
          autoComplete="off-git-credential"
          disabled={!canWrite || credQ.isLoading || credQ.error !== undefined}
          onChange={(v) => setToken(v)}
        />
        <CredentialStatus mask={mask} />
      </div>
      {error && (
        <div className="field-err" role="alert">
          {error}
        </div>
      )}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 12,
          marginTop: 20
        }}
      >
        <button
          className="btn"
          type="button"
          disabled={testAction?.disabled ?? false}
          onClick={() => testAction?.run()}
        >
          {testAction?.busy ? 'Testing…' : 'Test connection'}
        </button>
        <button
          className="btn primary sm"
          type="button"
          disabled={!canWrite || !dirty || (fieldsChanged && !pending?.verified) || busy}
          onClick={() => void onSave()}
        >
          {busy ? 'Saving…' : 'Save connection'}
        </button>
      </div>
      {fieldsChanged && !pending?.verified && (
        <p className="field-help" style={{ marginTop: 8 }}>
          Test the connection before saving.
        </p>
      )}
    </div>
  );
}


function CodexKeySection({ projectId, canWrite }: { projectId: string; canWrite: boolean }): JSX.Element {
  const credQ = useGetProjectCredentialQuery({ projectId, credential_type: 'codex_api_key' });
  const [key, setKey] = useState('');
  const [dirty, setDirty] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [setCredential, { isLoading }] = useSetProjectCredentialMutation();

  const mask = credQ.data;

  function onChange(v: string): void {
    setDirty(true);
    setSaved(false);
    setKey(v);
  }

  async function onSave(): Promise<void> {
    setError(null);
    setSaved(false);
    try {
      await setCredential({
        projectId,
        credential_type: 'codex_api_key',
        plaintext: key.trim()
      }).unwrap();
      setSaved(true);
      setDirty(false);
      setKey('');
    } catch (err) {
      setError(parseApiError(err).message);
    }
  }

  return (
    <div>
      {saved && (
        <div className="banner success" role="status">
          <span className="banner-icon">✓</span>
          <span>Codex API key saved.</span>
        </div>
      )}
      <CredentialAccessNotice error={credQ.error} />
      <div className="field">
        <label className="field-label" htmlFor="codex-key">
          API key
        </label>
        <PasswordInput
          id="codex-key"
          className="mono"
          toggleLabel="API key"
          invalid={!!error}
          placeholder={maskLabel(mask) ?? 'sk-…'}
          {...(mask?.is_set && mask.length !== undefined ? { maskLength: mask.length } : {})}
          {...(maskLabel(mask) !== null ? { maskHint: maskLabel(mask) as string } : {})}
          value={key}
          autoComplete="off-codex-key"
          disabled={!canWrite || credQ.isLoading || credQ.error !== undefined}
          onChange={onChange}
        />
        <CredentialStatus mask={mask} />
        {error && <div className="field-err">{error}</div>}
      </div>
      <div className="actions">
        <button
          className="btn primary sm"
          type="button"
          disabled={!canWrite || !dirty || key.trim() === '' || isLoading}
          onClick={() => void onSave()}
        >
          {isLoading ? 'Saving…' : 'Save key'}
        </button>
      </div>
    </div>
  );
}

function BasicInfoSection({ projectId, canWrite }: { projectId: string; canWrite: boolean }): JSX.Element {
  const detailQ = useGetProjectDetailQuery(projectId);
  const [updateProject, { isLoading }] = useUpdateProjectMutation();
  const [saved, setSaved] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const detail = detailQ.data;

  async function saveField(field: 'name' | 'description', value: string): Promise<void> {
    setError(null);
    try {
      await updateProject({ projectId, body: { [field]: value } }).unwrap();
      setSaved('saved');
    } catch (err) {
      setError(parseApiError(err).message);
    }
  }

  return (
    <div className="panel">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 18 }}>
        <div>
          <h2 className="panel-title">Basic info</h2>
        </div>
        {saved && (
          <span className="saved-pill">
            <span className="dot" />
            {saved}
          </span>
        )}
      </div>
      <div className="field-row">
        <div className="field">
          <label className="field-label" htmlFor="set-name">
            Project name
          </label>
          <input
            id="set-name"
            className="input"
            autoComplete="off-set-name"
            defaultValue={detail?.name ?? ''}
            readOnly={!canWrite}
            disabled={isLoading}
            onBlur={(e) => canWrite && saveField('name', e.target.value)}
          />
        </div>
        <div className="field">
          <label className="field-label">Slug</label>
          <input className="input mono readonly" value={detail?.slug ?? ''} readOnly tabIndex={-1} />
        </div>
      </div>
      <div className="field">
        <label className="field-label" htmlFor="set-desc">
          Description
        </label>
        <textarea
          id="set-desc"
          className="textarea"
          defaultValue={detail?.description ?? ''}
          readOnly={!canWrite}
          onBlur={(e) => canWrite && saveField('description', e.target.value)}
        />
      </div>
      {error && (
        <div className="field-err" role="alert">
          {error}
        </div>
      )}
    </div>
  );
}

function TechStackSettings({ projectId, canWrite }: { projectId: string; canWrite: boolean }): JSX.Element {
  const detailQ = useGetProjectDetailQuery(projectId);
  const [updateProject] = useUpdateProjectMutation();
  const toast = useToast();
  const [stack, setStack] = useState<TechStack | null>(null);

  const detail = detailQ.data;
  const locked = !canWrite || detail?.tech_stack_editable === false;
  const value = stack ?? detail?.tech_stack ?? emptyTechStack();

  async function persist(next: TechStack): Promise<void> {
    try {
      await updateProject({ projectId, body: { tech_stack: next } }).unwrap();
      toast.success('Tech stack updated.');
    } catch (err) {
      toast.error(parseApiError(err).message);
    }
  }

  return (
    <TechStackHelper
      value={value}
      locked={locked}
      onChange={(next) => {
        setStack(next);
        if (!locked) void persist(next);
      }}
    />
  );
}

function ConventionsSettings({ projectId, canWrite }: { projectId: string; canWrite: boolean }): JSX.Element {
  const list = useListConventionsQuery(projectId);
  const [upload, uploadState] = useUploadConventionMutation();
  const [del] = useDeleteConventionMutation();
  const toast = useToast();
  const stored = list.data?.conventions ?? [];

  async function onAdd(files: File[]): Promise<void> {
    const known = new Set(stored.map((c) => c.file_name));
    let uploaded = 0;
    for (const f of files) {
      if (known.has(f.name)) continue;
      try {
        await upload({ projectId, file: f }).unwrap();
        uploaded += 1;
      } catch (err) {
        toast.error(`${f.name}: ${parseApiError(err).message}`);
      }
    }
    if (uploaded > 0) {
      toast.success(uploaded === 1 ? 'Convention uploaded.' : `${uploaded} conventions uploaded.`);
    }
  }

  async function onDelete(conventionId: string, fileName: string): Promise<void> {
    try {
      await del({ projectId, conventionId }).unwrap();
      toast.success(`${fileName} removed.`);
    } catch (err) {
      toast.error(parseApiError(err).message);
    }
  }

  return (
    <div data-testid="conventions-settings">
      {canWrite ? (
        <ConventionUploadHelper onChange={(files) => void onAdd(files)} />
      ) : (
        <p className="text-text-muted text-[12.5px]">You don’t have permission to change conventions.</p>
      )}
      {uploadState.isError ? (
        <p className="text-err text-[12px] mt-2">Upload failed — check the file and try again.</p>
      ) : null}
      <div className="mt-4">
        <div className="text-[11.5px] text-text-muted uppercase tracking-wider mb-2">Stored conventions</div>
        {stored.length === 0 ? (
          <p className="text-text-muted text-[12.5px]" data-testid="conventions-empty">
            No convention files uploaded yet.
          </p>
        ) : (
          <ul className="flex flex-col gap-1.5" data-testid="conventions-list">
            {stored.map((c) => (
              <li
                key={c.convention_id}
                className="flex items-center gap-2 text-[12.5px] bg-panel-2 border border-border rounded-md px-3 py-1.5"
              >
                <span className="font-mono">{c.file_name}</span>
                <span className="text-text-muted">· {c.convention_type} · {Math.max(1, Math.round(c.size_bytes / 1024))} KB</span>
                {canWrite ? (
                  <button
                    type="button"
                    className="ml-auto text-[11px] text-err hover:underline"
                    onClick={() => void onDelete(c.convention_id, c.file_name)}
                    data-testid={`convention-delete-${c.convention_id}`}
                  >
                    Remove
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
